import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

// ─── The copied client ─────────────────────────────────────────────────────────
//
// "Copy client" on a booking puts one person on a clipboard; the empty-slot menu
// then offers "Paste <name>", which books them again under another therapy
// without searching for them a second time. That is the whole feature.
//
// IDENTITY ONLY — an id and a display name. Never their settings, notes, package
// or the booking it was copied from: those belong to the appointment it came
// from, and carrying them forward would silently reuse last session's decisions
// on a different treatment.
//
// SESSION-SCOPED, NEVER localStorage. This is the same rule as the chat
// composer's customer pin, for the same reason: a clipboard that survives a
// reload is a clipboard that is stale, and a stale one pastes yesterday's
// customer into today's slot. The operator sees a chip while one is held and can
// clear it in a click, so it is never invisible — a copy nobody can see is a
// booking made for the wrong person.

export interface CopiedClient {
  id: string
  name: string
}

interface CopiedClientValue {
  client: CopiedClient | null
  copy: (client: CopiedClient) => void
  clear: () => void
  /**
   * False outside a provider. Menus check it before offering "Copy client":
   * without a provider the copy would go nowhere, and a button that silently
   * does nothing is worse than no button.
   */
  available: boolean
}

const CopiedClientContext = createContext<CopiedClientValue>({
  client: null,
  copy: () => {},
  clear: () => {},
  available: false,
})

export function CopiedClientProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<CopiedClient | null>(null)

  const copy = useCallback((next: CopiedClient) => setClient(next), [])
  const clear = useCallback(() => setClient(null), [])

  const value = useMemo(
    () => ({ client, copy, clear, available: true }),
    [client, copy, clear],
  )
  return <CopiedClientContext.Provider value={value}>{children}</CopiedClientContext.Provider>
}

/**
 * Safe outside the provider — it reads as "nothing copied" and `available:
 * false`, so a calendar drawn in customer-facing chat neither offers Copy nor
 * Paste.
 */
export function useCopiedClient(): CopiedClientValue {
  return useContext(CopiedClientContext)
}
